import { z } from "zod";
import type { AdPlatform, Channel, ConnectionStatus } from "../enums";

// Integrations (WP5): Meta, TikTok and Google Ads connections, asset selection, server side conversion settings,
// WhatsApp embedded signup and the linked ad accounts. Shared by the API (validation) and the web app (types).
// Names are prefixed (Meta, TikTok, Google, Integration, Linked) to stay unique across the shared package.

const graphId = z.string().trim().regex(/^\d{1,32}$/, "Identifiant invalide");
const tiktokId = z.string().trim().regex(/^\d{1,32}$/, "Identifiant TikTok invalide");
const googleCustomerId = z
  .string()
  .trim()
  .transform((v) => v.replace(/-/g, ""))
  .pipe(z.string().regex(/^\d{6,12}$/, "Identifiant de compte Google Ads invalide"));

export const integrationTestEventCode = z
  .string()
  .trim()
  .max(64, "64 caractères maximum")
  .regex(/^[A-Za-z0-9_-]*$/, "Code de test invalide (lettres, chiffres, tirets)");

const urlTemplate = z
  .string()
  .trim()
  .max(500, "500 caractères maximum")
  .refine((v) => /^https?:\/\/[^\s]+$/i.test(v), "Adresse invalide : elle doit commencer par https://");

// ---------------------------------------------------------------------------------------------------------------
// Inputs

export const metaSelectionSchema = z.object({
  adAccountIds: z.array(graphId).max(100).default([]),
  pageIds: z.array(graphId).max(100).default([]),
  igAccountIds: z.array(graphId).max(100).default([]),
  phoneNumberIds: z.array(graphId).max(50).default([]),
  pixelId: graphId.nullable().default(null),
  catalogId: graphId.nullable().default(null),
  businessId: graphId.nullable().default(null),
});
export type MetaSelectionInput = z.infer<typeof metaSelectionSchema>;

export const metaSettingsSchema = z
  .object({
    capiEnabled: z.boolean().optional(),
    sendHashedContacts: z.boolean().optional(),
    testEventCode: integrationTestEventCode.nullable().optional(),
    catalogSyncEnabled: z.boolean().optional(),
    leadsSyncEnabled: z.boolean().optional(),
    catalogUrlTemplate: urlTemplate.nullable().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), "Aucun réglage à modifier");
export type MetaSettingsInput = z.infer<typeof metaSettingsSchema>;

export const WHATSAPP_SIGNUP_EVENTS = [
  "FINISH",
  "FINISH_ONLY_WABA",
  "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING",
  "FINISH_OBO_MIGRATION",
  "FINISH_GRANT_ONLY_API_ACCESS",
] as const;
export type WhatsAppSignupEvent = (typeof WHATSAPP_SIGNUP_EVENTS)[number];

const whatsappPin = z.string().trim().regex(/^\d{6}$/, "Le code PIN doit contenir exactement 6 chiffres");

export const whatsappEmbeddedSignupSchema = z.object({
  code: z.string().trim().min(8, "Code d'autorisation invalide").max(2048),
  wabaId: graphId,
  phoneNumberId: graphId,
  pin: whatsappPin.optional(),
  sessionEvent: z.enum(WHATSAPP_SIGNUP_EVENTS).optional(),
});
export type WhatsAppEmbeddedSignupInput = z.infer<typeof whatsappEmbeddedSignupSchema>;

export const whatsappRegisterSchema = z.object({ pin: whatsappPin });
export type WhatsAppRegisterInput = z.infer<typeof whatsappRegisterSchema>;

export const integrationCapiTestSchema = z.object({
  testEventCode: integrationTestEventCode.min(1, "Code de test requis").optional(),
});
export type IntegrationCapiTestInput = z.infer<typeof integrationCapiTestSchema>;

export const metaLeadFormSyncSchema = z.object({
  pageId: graphId,
  sinceDays: z.number().int().min(1).max(90).default(90),
});
export type MetaLeadFormSyncInput = z.infer<typeof metaLeadFormSyncSchema>;

export const tiktokSelectionSchema = z.object({
  advertiserIds: z.array(tiktokId).max(50).default([]),
  pixelCode: z
    .string()
    .trim()
    .max(64)
    .regex(/^[A-Za-z0-9]+$/, "Code pixel invalide")
    .nullable()
    .default(null),
});
export type TikTokSelectionInput = z.infer<typeof tiktokSelectionSchema>;

export const tiktokSettingsSchema = z
  .object({
    eventsApiEnabled: z.boolean().optional(),
    sendHashedContacts: z.boolean().optional(),
    testEventCode: integrationTestEventCode.nullable().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), "Aucun réglage à modifier");
export type TikTokSettingsInput = z.infer<typeof tiktokSettingsSchema>;

export const googleSelectionSchema = z.object({
  customerIds: z.array(googleCustomerId).max(50).default([]),
});
export type GoogleSelectionInput = z.infer<typeof googleSelectionSchema>;

export const linkedAdAccountUpdateSchema = z.object({ enabled: z.boolean() });
export type LinkedAdAccountUpdateInput = z.infer<typeof linkedAdAccountUpdateSchema>;

// ---------------------------------------------------------------------------------------------------------------
// DTOs returned by /api/integrations

export type IntegrationHealth = "ok" | "expiring" | "expired" | "invalid" | "unknown";
export type IntegrationConnectionState = ConnectionStatus | "not_connected";

export const INTEGRATION_STATE_LABELS: Record<IntegrationConnectionState, string> = {
  active: "Connecté",
  expired: "Expiré",
  revoked: "Déconnecté",
  error: "Erreur",
  pending: "En attente",
  not_connected: "Non connecté",
};

export type LinkedAdAccountStatus = "active" | "disabled" | "unsettled" | "pending" | "closed" | "unknown";

export const LINKED_AD_ACCOUNT_STATUS_LABELS: Record<LinkedAdAccountStatus, string> = {
  active: "Actif",
  disabled: "Désactivé",
  unsettled: "Impayé",
  pending: "En vérification",
  closed: "Fermé",
  unknown: "Inconnu",
};

export interface IntegrationsConfigDto {
  graphVersion: string;
  metaAppId: string | null;
  whatsappConfigId: string | null;
  webhookUrl: string;
  providers: {
    meta: boolean;
    whatsappEmbeddedSignup: boolean;
    tiktok: boolean;
    google: boolean;
  };
}

export interface IntegrationChannelCounts {
  active: number;
  error: number;
}

export interface IntegrationsSummaryDto {
  meta: {
    configured: boolean;
    state: IntegrationConnectionState;
    health: IntegrationHealth;
    accountName: string | null;
    adAccounts: number;
    pages: number;
    pixelSelected: boolean;
    catalogSelected: boolean;
  };
  tiktok: { configured: boolean; state: IntegrationConnectionState; adAccounts: number; pixelSelected: boolean };
  google: { configured: boolean; state: IntegrationConnectionState; adAccounts: number };
  channels: Record<Channel, IntegrationChannelCounts>;
  whatsappEmbeddedSignup: boolean;
  carriers: { active: number; failed: number; total: number };
  payments: { active: number; failed: number; total: number };
  ai: { mode: "platform" | "byok"; provider: string | null };
}

export interface MetaBusinessAsset {
  id: string;
  name: string;
  verificationStatus: string | null;
}

export interface MetaAdAccountAsset {
  id: string;
  name: string;
  currency: string;
  timezone: string;
  status: LinkedAdAccountStatus;
  statusReason: string | null;
  businessId: string | null;
  businessName: string | null;
}

export interface MetaPageAsset {
  id: string;
  name: string;
  category: string;
  pictureUrl: string | null;
  tasks: string[];
  hasToken: boolean;
  instagramAccountId: string | null;
}

export interface MetaInstagramAsset {
  id: string;
  username: string;
  name: string;
  pictureUrl: string | null;
  pageId: string | null;
}

export interface MetaWabaAsset {
  id: string;
  name: string;
  businessId: string | null;
  source: "login" | "embedded_signup";
}

export interface MetaPhoneNumberAsset {
  id: string;
  wabaId: string;
  displayPhoneNumber: string;
  verifiedName: string;
  qualityRating: string | null;
  status: string | null;
  platformType: string | null;
}

export interface MetaPixelAsset {
  id: string;
  name: string;
  adAccountId: string | null;
  lastFiredAt: string | null;
}

export interface MetaCatalogAsset {
  id: string;
  name: string;
  productCount: number | null;
  businessId: string | null;
}

export interface MetaSelectionDto {
  adAccountIds: string[];
  pageIds: string[];
  igAccountIds: string[];
  phoneNumberIds: string[];
  pixelId: string | null;
  catalogId: string | null;
  businessId: string | null;
}

export interface MetaSettingsDto {
  capiEnabled: boolean;
  sendHashedContacts: boolean;
  testEventCode: string | null;
  catalogSyncEnabled: boolean;
  leadsSyncEnabled: boolean;
  catalogUrlTemplate: string | null;
}

/** Channel account linked through Meta, with its webhook subscription state. */
export interface MetaChannelSummary {
  id: string;
  channel: Channel;
  externalId: string;
  name: string;
  username: string;
  phoneDisplay: string | null;
  pictureUrl: string | null;
  status: "active" | "error" | "disabled";
  subscribedAt: string | null;
  lastError: string | null;
  wabaId: string | null;
  pageId: string | null;
  qualityRating: string | null;
}

export interface MetaCatalogSyncSummary {
  catalogId: string | null;
  enabled: boolean;
  urlTemplate: string | null;
  lastRunAt: string | null;
  lastFinishedAt: string | null;
  lastError: string | null;
  pendingBatches: number;
  counts: { synced: number; pending: number; error: number; notSynced: number };
  recentErrors: Array<{ productId: string; name: string; error: string }>;
}

export interface MetaStatusDto {
  configured: boolean;
  connected: boolean;
  state: IntegrationConnectionState;
  health: IntegrationHealth;
  accountName: string | null;
  scopes: string[];
  tokenExpiresAt: string | null;
  dataAccessExpiresAt: string | null;
  tokenCheckedAt: string | null;
  tokenWarning: string | null;
  lastError: string | null;
  lastSyncAt: string | null;
  connectedAt: string | null;
  assets: {
    businesses: MetaBusinessAsset[];
    adAccounts: MetaAdAccountAsset[];
    pages: MetaPageAsset[];
    igAccounts: MetaInstagramAsset[];
    wabas: MetaWabaAsset[];
    phoneNumbers: MetaPhoneNumberAsset[];
    pixels: MetaPixelAsset[];
    catalogs: MetaCatalogAsset[];
  };
  selected: MetaSelectionDto;
  settings: MetaSettingsDto;
  channels: MetaChannelSummary[];
  catalog: MetaCatalogSyncSummary;
}

export type WhatsAppRegistrationOutcome = "registered" | "already_registered" | "skipped" | "pin_required" | "too_many_attempts" | "failed";

export interface WhatsAppSignupResultDto {
  channelAccountId: string;
  phoneNumberId: string;
  displayPhoneNumber: string;
  verifiedName: string;
  subscribed: boolean;
  registration: WhatsAppRegistrationOutcome;
  message: string;
}

export interface IntegrationCapiTestResultDto {
  ok: boolean;
  eventsReceived: number | null;
  message: string;
}

export interface MetaLeadFormDto {
  id: string;
  name: string;
  status: string;
  leadsCount: number | null;
  createdAt: string | null;
  pageId: string;
  pageName: string;
}

export interface MetaLeadFormsDto {
  items: MetaLeadFormDto[];
  errors: Array<{ pageId: string; pageName: string; message: string }>;
}

export interface MetaLeadSyncResultDto {
  imported: number;
  skipped: number;
  total: number;
}

export interface TikTokAdvertiserAsset {
  id: string;
  name: string;
  currency: string;
  timezone: string;
  status: LinkedAdAccountStatus;
  rawStatus: string | null;
  country: string | null;
}

export interface TikTokPixelAsset {
  id: string;
  code: string;
  name: string;
  advertiserId: string;
  activityStatus: string | null;
}

export interface TikTokStatusDto {
  configured: boolean;
  connected: boolean;
  state: IntegrationConnectionState;
  health: IntegrationHealth;
  scopes: string[];
  lastError: string | null;
  lastSyncAt: string | null;
  connectedAt: string | null;
  assets: { advertisers: TikTokAdvertiserAsset[]; pixels: TikTokPixelAsset[] };
  selected: { advertiserIds: string[]; pixelCode: string | null };
  settings: { eventsApiEnabled: boolean; sendHashedContacts: boolean; testEventCode: string | null };
}

export interface GoogleCustomerAsset {
  id: string;
  name: string;
  currency: string;
  timezone: string;
  manager: boolean;
  testAccount: boolean;
  status: LinkedAdAccountStatus;
  rawStatus: string | null;
  loginCustomerId: string;
  parentId: string | null;
}

export interface GoogleAdsStatusDto {
  configured: boolean;
  connected: boolean;
  state: IntegrationConnectionState;
  health: IntegrationHealth;
  scopes: string[];
  lastError: string | null;
  lastSyncAt: string | null;
  connectedAt: string | null;
  assets: { customers: GoogleCustomerAsset[] };
  selected: { customerIds: string[] };
}

export interface LinkedAdAccountDto {
  id: string;
  platform: AdPlatform;
  externalId: string;
  name: string;
  currency: string;
  timezone: string;
  status: LinkedAdAccountStatus;
  statusReason: string | null;
  businessName: string | null;
  enabled: boolean;
  lastStructureSyncAt: string | null;
  lastInsightsSyncAt: string | null;
  lastError: string | null;
  createdAt: string;
}
